import {ReactNode} from 'react';
import {
    createBrowserRouter,
    RouterProvider
} from 'react-router-dom';
import {QueryClientProvider} from '@tanstack/react-query';
import {
    CognitoContextProvider,
    createAuthAwareQueryClient,
    SessionGuardProvider,
    VerifiedPermissionsProvider
} from '@flexinet/auth-components';
import {UserAttributesContextProvider} from "./context/UserAttributesContext.tsx";
import {theme} from "./theme.ts";
import {MantineProvider} from "@mantine/core";
import {Notifications} from "@mantine/notifications";
import {ModalsProvider} from "@mantine/modals";

import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';

import {AuthProvider} from "./context/AuthContext.tsx";
import {routes} from './routes.tsx';
import {useAuth} from './hooks/useAuth.ts';
import permissions from './config/permissions.ts';
import {actions} from './config/rbac/actions.ts';

const queryClient = createAuthAwareQueryClient();

// FNP-1731: the shared provider evaluates permissions only once this app's own
// AuthContext has resolved the session, and again whenever the user changes.
const AppVerifiedPermissionsProvider = ({children}: { children?: ReactNode }) => {
    const {status, user} = useAuth();

    return (
        <VerifiedPermissionsProvider
            actions={actions}
            policyStoreId={permissions.policyStoreId}
            region={permissions.region}
            actionType={'Action'}
            enabled={permissions.enabled}
            sessionStatus={status}
            sessionKey={status === 'authenticated' ? user?.userId : undefined}
        >
            {children}
        </VerifiedPermissionsProvider>
    );
};

const router = createBrowserRouter(routes)


// FNP-1731: the provider tree is exported on its own so a test can mount it
// exactly as the app does, without the router.
export const AppProviders = ({children}: { children?: ReactNode }) => {

    return (
        <MantineProvider theme={theme}>
            <SessionGuardProvider>
                <QueryClientProvider client={queryClient}>
                    <AuthProvider>
                        <CognitoContextProvider>
                            <AppVerifiedPermissionsProvider>
                                <UserAttributesContextProvider>
                                    <ModalsProvider>
                                        <Notifications position={'bottom-left'}/>
                                        {children}
                                    </ModalsProvider>
                                </UserAttributesContextProvider>
                            </AppVerifiedPermissionsProvider>
                        </CognitoContextProvider>
                    </AuthProvider>
                </QueryClientProvider>
            </SessionGuardProvider>
        </MantineProvider>
    );
}

export const App = () => {

    return (
        <AppProviders>
            <RouterProvider router={router}/>
        </AppProviders>
    );
}

